/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Log } from "../../viewModel/logging";
import { canNavigateBackInHistory } from "../../viewModel/navigation";

const TAG: string = "BrowserBackStop";

const BACK_STOP_STATE_KEY: string = "backStop";

/**
 * Whether a router location state marks the back-stop entry pushed by
 * {@link useBrowserBackStop}. `NavigationInterceptor` uses it to let that one
 * same-path push through its duplicate-navigation guard without side-effects.
 */
export function isBackStopNavigationState(state: unknown): boolean {
  if (state === null || typeof state !== "object") {
    return false;
  }
  return (state as Record<string, unknown>)[BACK_STOP_STATE_KEY] === true;
}

/**
 * Makes the browser Back button a silent no-op on a root screen (UAW-1129).
 *
 * When the screen is the first history entry of this document there is
 * nothing in the app to go back to, so Back leaves the document — into
 * whatever the tab showed before. After a typed URL that is the previous load
 * of this same app, which cold-boots through the splash loader (and, when the
 * reload cannot restore the session, the sign-in screen).
 *
 * The hook pushes one duplicate entry for the current path so Back has an
 * in-app entry to land on. That Back is a POP to the same path, which
 * `NavigationInterceptor`'s duplicate-navigation guard already resets, so the
 * press is swallowed and the user stays on the screen.
 *
 * Two constraints shape how the entry is pushed:
 *
 *   - It is pushed on the user's first keydown/pointerdown, never on mount.
 *     Chromium's back-button intervention skips history entries a page adds
 *     without user activation, so an entry pushed on mount would be jumped
 *     over by Back.
 *   - It is pushed synchronously in the capture phase, before the gesture's
 *     own handlers run. React Router holds one blocked navigation at a time
 *     and the latest one replaces it, so if the same gesture also navigates
 *     (Enter on a menu item), that navigation supersedes the back-stop rather
 *     than the reverse. The listeners stay attached until an entry exists
 *     behind the current one, so a superseded back-stop is retried on the
 *     next gesture.
 *
 * @param isEnabled - false disables the hook (TV devices, where Back is a
 *   remote key handled by the app, not browser history).
 */
export function useBrowserBackStop(isEnabled: boolean): void {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect((): (() => void) => {
    if (!isEnabled || canNavigateBackInHistory()) {
      return (): void => {
        // No cleanup required
      };
    }

    const removeListeners = (): void => {
      window.removeEventListener("keydown", installBackStop, true);
      window.removeEventListener("pointerdown", installBackStop, true);
    };

    function installBackStop(): void {
      if (canNavigateBackInHistory()) {
        removeListeners();
        return;
      }
      Log.d(TAG, "installBackStop() pushing back-stop entry for " + pathname);
      navigate(pathname, { state: { [BACK_STOP_STATE_KEY]: true } });
    }

    window.addEventListener("keydown", installBackStop, true);
    window.addEventListener("pointerdown", installBackStop, true);
    return removeListeners;
  }, [isEnabled, navigate, pathname]);
}
